"use client";
import { addGlobalEventListener, canUseDOM, hasOwnProperty } from "@ariakit/utils";
import * as React from "react";
import { isValidElement, useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from "react";
import { jsx } from "react/jsx-runtime";
//#region src/misc.ts
/**
* Helpers for working with refs, elements, and props.
* @module General utilities
*/
/**
* Sets both a function and object React ref.
*
* Returns a callback ref cleanup function when one is provided.
*/
function setRef(ref, value) {
	if (typeof ref === "function") {
		const cleanup = ref(value);
		if (typeof cleanup === "function") return cleanup;
	} else if (ref) ref.current = value;
}
/**
* Checks if an element is a valid React element with a ref.
*/
function isValidElementWithRef(element) {
	if (!element) return false;
	if (!isValidElement(element)) return false;
	if ("ref" in element.props) return true;
	if ("ref" in element) return true;
	return false;
}
/**
* Gets the ref property from a React element.
*/
function getRefProperty(element) {
	if (!isValidElementWithRef(element)) return null;
	return { ...element.props }.ref || element.ref;
}
/**
* Merges two sets of props.
*/
function mergeProps(base, overrides) {
	const props = { ...base };
	for (const key in overrides) {
		if (!hasOwnProperty(overrides, key)) continue;
		if (key === "className") {
			const prop = "className";
			const baseClass = base[prop];
			const overrideClass = overrides[prop];
			if (baseClass && overrideClass) props[prop] = `${baseClass} ${overrideClass}`;
			else props[prop] = overrideClass || baseClass;
			continue;
		}
		if (key === "style") {
			const prop = "style";
			props[prop] = base[prop] ? {
				...base[prop],
				...overrides[prop]
			} : overrides[prop];
			continue;
		}
		const overrideValue = overrides[key];
		if (key.startsWith("on")) {
			if (typeof overrideValue !== "function") continue;
			const baseValue = base[key];
			if (typeof baseValue === "function") {
				props[key] = (...args) => {
					overrideValue(...args);
					baseValue(...args);
				};
				continue;
			}
		}
		props[key] = overrideValue;
	}
	return props;
}
//#endregion
//#region src/hooks.ts
/**
* React hooks for refs, events, ids, effects, and element metadata.
* @module Hooks
*/
const _React = { ...React };
const useReactId = _React.useId;
const useReactDeferredValue = _React.useDeferredValue;
const useReactInsertionEffect = _React.useInsertionEffect;
/**
* `React.useLayoutEffect` that fallbacks to `React.useEffect` on server side.
*/
const useSafeLayoutEffect = canUseDOM ? useLayoutEffect : useEffect;
/**
* Returns a value that never changes even if the argument is updated.
* @example
* function Component({ prop }) {
*   const initialProp = useInitialValue(prop);
* }
*/
function useInitialValue(value) {
	const [initialValue] = useState(value);
	return initialValue;
}
/**
* Creates a `React.RefObject` that is constantly updated with the incoming
* value.
* @example
* function Component({ prop }) {
*   const propRef = useLiveRef(prop);
* }
*/
function useLiveRef(value) {
	const ref = useRef(value);
	useSafeLayoutEffect(() => {
		ref.current = value;
	});
	return ref;
}
/**
* Creates a stable callback function that has access to the latest state and
* can be used within event handlers and effect callbacks. Throws when used in
* the render phase.
* @example
* function Component(props) {
*   const onClick = useEvent(props.onClick);
*   React.useEffect(() => {}, [onClick]);
* }
*/
function useEvent(callback) {
	const ref = useRef(() => {
		throw new Error("Cannot call an event handler while rendering.");
	});
	if (useReactInsertionEffect) useReactInsertionEffect(() => {
		ref.current = callback;
	});
	else ref.current = callback;
	return useCallback((...args) => ref.current?.(...args), []);
}
/**
* Creates a React state that calls a callback function whenever the state
* changes and rolls back to the previous state on cleanup.
*/
function useTransactionState(callback) {
	const [state, setState] = useState(null);
	useSafeLayoutEffect(() => {
		if (state == null) return;
		if (!callback) return;
		let prevState = null;
		callback((prev) => {
			prevState = prev;
			return state;
		});
		return () => {
			callback(prevState);
		};
	}, [state, callback]);
	return [state, setState];
}
/**
* Merges React Refs into a single memoized function ref so you can pass it to
* an element.
* @example
* const Component = React.forwardRef((props, ref) => {
*   const internalRef = React.useRef();
*   return <div {...props} ref={useMergeRefs(internalRef, ref)} />;
* });
*/
function useMergeRefs(...refs) {
	return useMemo(() => {
		if (!refs.some(Boolean)) return;
		return (value) => {
			const refEffects = [];
			for (const ref of refs) {
				if (!ref) continue;
				const cleanup = setRef(ref, value);
				refEffects.push({
					ref,
					cleanup: typeof cleanup === "function" ? cleanup : void 0
				});
			}
			if (!refEffects.some((effect) => effect.cleanup)) return;
			return () => {
				for (const { ref, cleanup } of refEffects) if (cleanup) cleanup();
				else setRef(ref, null);
			};
		};
	}, refs);
}
/**
* Generates a unique ID. Uses React's useId if available.
*/
function useId(defaultId) {
	if (useReactId) {
		const reactId = useReactId();
		if (defaultId) return defaultId;
		return reactId;
	}
	const [id, setId] = useState(defaultId);
	useSafeLayoutEffect(() => {
		if (defaultId || id) return;
		setId(`id-${Math.random().toString(36).slice(2, 8)}`);
	}, [defaultId, id]);
	return defaultId || id;
}
/**
* Uses React's useDeferredValue if available.
*/
function useDeferredValue(value) {
	if (useReactDeferredValue) return useReactDeferredValue(value);
	const [deferredValue, setDeferredValue] = useState(value);
	useEffect(() => {
		const raf = requestAnimationFrame(() => setDeferredValue(value));
		return () => cancelAnimationFrame(raf);
	}, [value]);
	return deferredValue;
}
/**
* Returns the tag name by parsing an element ref.
* @example
* function Component(props) {
*   const ref = React.useRef();
*   const tagName = useTagName(ref, "button"); // div
*   return <div ref={ref} {...props} />;
* }
*/
function useTagName(refOrElement, type) {
	const stringOrUndefined = (type) => {
		if (typeof type !== "string") return;
		return type;
	};
	const [tagName, setTagName] = useState(() => stringOrUndefined(type));
	useSafeLayoutEffect(() => {
		setTagName((refOrElement && "current" in refOrElement ? refOrElement.current : refOrElement)?.tagName.toLowerCase() || stringOrUndefined(type));
	}, [refOrElement, type]);
	return tagName;
}
/**
* Returns the attribute value of an element.
* @example
* function Component(props) {
*   const ref = React.useRef();
*   const role = useAttribute(ref, "role", props.role);
*   return <div ref={ref} {...props} />;
* }
*/
function useAttribute(refOrElement, attributeName, defaultValue) {
	const initialValue = useInitialValue(defaultValue);
	const [attribute, setAttribute] = useState(initialValue);
	useEffect(() => {
		const element = refOrElement && "current" in refOrElement ? refOrElement.current : refOrElement;
		if (!element) return;
		const callback = () => {
			const value = element.getAttribute(attributeName);
			setAttribute(value == null ? initialValue : value);
		};
		const observer = new MutationObserver(callback);
		observer.observe(element, { attributeFilter: [attributeName] });
		callback();
		return () => observer.disconnect();
	}, [
		refOrElement,
		attributeName,
		initialValue
	]);
	return attribute;
}
/**
* A `React.useEffect` that will not run on the first render.
*/
function useUpdateEffect(effect, deps) {
	const mounted = useRef(false);
	useEffect(() => {
		if (mounted.current) return effect();
		mounted.current = true;
	}, deps);
	useEffect(() => () => {
		mounted.current = false;
	}, []);
}
/**
* A `React.useLayoutEffect` that will not run on the first render.
*/
function useUpdateLayoutEffect(effect, deps) {
	const mounted = useRef(false);
	useSafeLayoutEffect(() => {
		if (mounted.current) return effect();
		mounted.current = true;
	}, deps);
	useSafeLayoutEffect(() => () => {
		mounted.current = false;
	}, []);
}
/**
* A React hook similar to `useState` and `useReducer`, but with the only
* purpose of re-rendering the component.
*/
function useForceUpdate() {
	return useReducer(() => [], []);
}
/**
* Returns an event callback similar to `useEvent`, but this also accepts a
* boolean value, which will be turned into a function.
*/
function useBooleanEvent(booleanOrCallback) {
	return useEvent(typeof booleanOrCallback === "function" ? booleanOrCallback : () => booleanOrCallback);
}
/**
* Returns props with an additional `wrapElement` prop.
*/
function useWrapElement(props, callback, deps = []) {
	const wrapElement = useCallback((element) => {
		if (props.wrapElement) element = props.wrapElement(element);
		return callback(element);
	}, [...deps, props.wrapElement]);
	return {
		...props,
		wrapElement
	};
}
/**
* Merges the portalRef prop and returns a `domReady` to be used in the
* components that use Portal underneath.
*/
function usePortalRef(portalProp = false, portalRefProp) {
	const [portalNode, setPortalNode] = useState(null);
	return {
		portalRef: useMergeRefs(setPortalNode, portalRefProp),
		portalNode,
		domReady: !portalProp || portalNode
	};
}
/**
* A hook that passes metadata props around without leaking them to the DOM.
*/
function useMetadataProps(props, key, value) {
	const parent = props.onLoadedMetadataCapture;
	const onLoadedMetadataCapture = useMemo(() => {
		return Object.assign(() => {}, parent, ...value !== void 0 ? [{ [key]: value }] : []);
	}, [
		parent,
		key,
		value
	]);
	return [parent?.[key], { onLoadedMetadataCapture }];
}
let hasInstalledGlobalEventListeners = false;
/**
* Returns a function that checks whether the mouse is moving.
*/
function useIsMouseMoving() {
	useEffect(() => {
		if (hasInstalledGlobalEventListeners) return;
		addGlobalEventListener("mousemove", setMouseMoving, true);
		addGlobalEventListener("mousedown", resetMouseMoving, true);
		addGlobalEventListener("mouseup", resetMouseMoving, true);
		addGlobalEventListener("keydown", resetMouseMoving, true);
		addGlobalEventListener("scroll", resetMouseMoving, true);
		hasInstalledGlobalEventListeners = true;
	}, []);
	return useEvent(() => mouseMoving);
}
let mouseMoving = false;
let previousScreenX = 0;
let previousScreenY = 0;
function hasMouseMovement(event) {
	const movementX = event.movementX || event.screenX - previousScreenX;
	const movementY = event.movementY || event.screenY - previousScreenY;
	previousScreenX = event.screenX;
	previousScreenY = event.screenY;
	return movementX || movementY || process.env.NODE_ENV === "test";
}
function setMouseMoving(event) {
	if (!hasMouseMovement(event)) return;
	mouseMoving = true;
}
function resetMouseMoving() {
	mouseMoving = false;
}
//#endregion
//#region src/system.tsx
/**
* The same as `React.forwardRef` but passes the `ref` as a prop and returns a
* component with the same generic type.
*/
function forwardRef(render) {
	const Role = React.forwardRef((props, ref) => render({
		...props,
		ref
	}));
	Role.displayName = render.displayName || render.name;
	return Role;
}
/**
* The same as `React.memo` but returns a component with the same generic type.
*/
function memo(Component, propsAreEqual) {
	return React.memo(Component, propsAreEqual);
}
/**
* Creates a React element that supports the `render` and `wrapElement` props.
*/
function createElement(Type, props) {
	const { wrapElement, render, ...rest } = props;
	const mergedRef = useMergeRefs(props.ref, getRefProperty(render));
	let element;
	if (React.isValidElement(render)) {
		const renderProps = {
			...render.props,
			ref: mergedRef
		};
		element = React.cloneElement(render, mergeProps(rest, renderProps));
	} else if (render) element = render(rest);
	else element = /* @__PURE__ */ jsx(Type, { ...rest });
	if (wrapElement) return wrapElement(element);
	return element;
}
/**
* Creates a component hook that accepts props and returns props so they can be
* passed to a React element.
*/
function createHook(useProps) {
	const useRole = (props = {}) => {
		return useProps(props);
	};
	useRole.displayName = useProps.name;
	return useRole;
}
/**
* Creates an Ariakit store context with hooks and provider components.
*/
function createStoreContext(providers = [], scopedProviders = []) {
	const context = React.createContext(void 0);
	const scopedContext = React.createContext(void 0);
	const useContext = () => React.useContext(context);
	const useScopedContext = (onlyScoped = false) => {
		const scoped = React.useContext(scopedContext);
		const store = useContext();
		if (onlyScoped) return scoped;
		return scoped || store;
	};
	const useProviderContext = () => {
		const scoped = React.useContext(scopedContext);
		const store = useContext();
		if (scoped && scoped === store) return;
		return store;
	};
	const ContextProvider = (props) => {
		return providers.reduceRight((children, Provider) => /* @__PURE__ */ jsx(Provider, {
			...props,
			children
		}), /* @__PURE__ */ jsx(context.Provider, { ...props }));
	};
	const ScopedContextProvider = (props) => {
		return /* @__PURE__ */ jsx(ContextProvider, {
			...props,
			children: scopedProviders.reduceRight((children, Provider) => /* @__PURE__ */ jsx(Provider, {
				...props,
				children
			}), /* @__PURE__ */ jsx(scopedContext.Provider, { ...props }))
		});
	};
	return {
		context,
		scopedContext,
		useContext,
		useScopedContext,
		useProviderContext,
		ContextProvider,
		ScopedContextProvider
	};
}
//#endregion
export { createElement, createHook, createStoreContext, forwardRef, getRefProperty, isValidElementWithRef, memo, mergeProps, setRef, useAttribute, useBooleanEvent, useDeferredValue, useEvent, useForceUpdate, useId, useInitialValue, useIsMouseMoving, useLiveRef, useMergeRefs, useMetadataProps, usePortalRef, useSafeLayoutEffect, useTagName, useTransactionState, useUpdateEffect, useUpdateLayoutEffect, useWrapElement };

//# sourceMappingURL=index.js.map